<template>
  <div class="min-h-screen bg-background">
    <Header v-if="showHeader" />
    <main :class="{ 'pb-16': showBottomNav, 'pb-24': showFloatingNav }">
      <slot />
    </main>
    <FlowBottomNav v-if="showBottomNav" />
    <WebFloatingNav v-if="showFloatingNav" />

    <!-- Smart-dine-in: Orders list — drawer on mobile, modal on desktop (router ?drawer=orders) -->
    <UiDrawer
      v-if="windowWidth < 768"
      :model-value="showOrdersDrawer"
      side="bottom"
      height="85vh"
      border-radius="20px"
      @update:model-value="handleOrdersDrawerUpdate"
    >
      <MyOrdersDrawerContent
        :open="showOrdersDrawer"
        @close="handleOrdersDrawerUpdate(false)"
        @order-closed="handleOrderClosed"
      />
    </UiDrawer>
    <Modal
      v-else
      :model-value="showOrdersDrawer"
      :show-actions="false"
      max-width="xl"
      @update:model-value="handleOrdersDrawerUpdate"
    >
      <MyOrdersDrawerContent
        :open="showOrdersDrawer"
        @close="handleOrdersDrawerUpdate(false)"
        @order-closed="handleOrderClosed"
      />
    </Modal>

    <!-- Smart-dine-in: Cart — drawer on mobile, modal on desktop (router ?drawer=order) -->
    <UiDrawer
      v-if="windowWidth < 768"
      :model-value="showOrderDrawer"
      side="bottom"
      height="80vh"
      border-radius="20px"
      @update:model-value="handleOrderDrawerUpdate"
    >
      <OrderConfirmation
        :cart="orderStore.cart"
        :can-place-order="canPlaceOrderFromStorage"
        :placing-order="isPlacingOrderFromDrawer"
        v-bind="{
          ...(reservationForDrawer?.tenantId && reservationForDrawer?.tableNo
            ? { tenantId: reservationForDrawer.tenantId, tableNo: reservationForDrawer.tableNo }
            : reservationForDrawer?.tenantId
              ? { tenantId: reservationForDrawer.tenantId }
              : {}),
          ...(reservationForDrawer?.reservationId != null
            ? { activeReservationId: reservationForDrawer.reservationId }
            : {}),
        }"
        @close="handleOrderDrawerUpdate(false)"
        @place-order="handlePlaceOrderFromDrawer"
        @login-click="handleOrderDrawerLogin"
        @signup-click="handleOrderDrawerSignup"
        @order-complete="handleOrderCompleteFromDrawer"
        @guest-success="handleGuestSuccessFromDrawer"
        @table-unavailable="handleTableUnavailableFromCart"
      />
    </UiDrawer>
    <Modal
      v-else
      :model-value="showOrderDrawer"
      :show-actions="false"
      max-width="md"
      @update:model-value="handleOrderDrawerUpdate"
    >
      <OrderConfirmation
        :cart="orderStore.cart"
        :can-place-order="canPlaceOrderFromStorage"
        :placing-order="isPlacingOrderFromDrawer"
        v-bind="{
          ...(reservationForDrawer?.tenantId && reservationForDrawer?.tableNo
            ? { tenantId: reservationForDrawer.tenantId, tableNo: reservationForDrawer.tableNo }
            : reservationForDrawer?.tenantId
              ? { tenantId: reservationForDrawer.tenantId }
              : {}),
          ...(reservationForDrawer?.reservationId != null
            ? { activeReservationId: reservationForDrawer.reservationId }
            : {}),
        }"
        @close="handleOrderDrawerUpdate(false)"
        @place-order="handlePlaceOrderFromDrawer"
        @login-click="handleOrderDrawerLogin"
        @signup-click="handleOrderDrawerSignup"
        @order-complete="handleOrderCompleteFromDrawer"
        @guest-success="handleGuestSuccessFromDrawer"
        @table-unavailable="handleTableUnavailableFromCart"
      />
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useOrderStore } from '@/stores/order'
import { useActiveReservation } from '@/composables/features/useActiveReservation'
import { useOrder } from '@/composables/features/useOrder'
import { useReservationSessionGuard } from '@/composables/features/useReservationSessionGuard'
import { useNotifications } from '@/composables/core/useNotifications'
import Header from '@/components/organisms/Header.vue'
import FlowBottomNav from '@/components/organisms/FlowBottomNav.vue'
import WebFloatingNav from '@/components/organisms/WebFloatingNav.vue'
import UiDrawer from '@/components/common/UiDrawer.vue'
import Modal from '@/components/common/Modal.vue'
import OrderConfirmation from '@/components/features/order/OrderConfirmation.vue'
import MyOrdersDrawerContent from '@/components/features/order/MyOrdersDrawerContent.vue'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const orderStore = useOrderStore()
const { getStoredActiveReservation } = useActiveReservation()
const { placeOrder } = useOrder()
const { showError } = useNotifications()
const { validateReservationStillActive } = useReservationSessionGuard()
const windowWidth = ref<number>(typeof window !== 'undefined' ? window.innerWidth : 1024)

const storedReservation = ref<ReturnType<typeof getStoredActiveReservation>>(null)
const sessionExpiredForOrderDrawer = ref<boolean>(false)
const isPlacingOrderFromDrawer = ref<boolean>(false)

const showOrderDrawer = computed(() => {
  if (route.name === 'RestaurantMenu') return false
  return route.query.drawer === 'order'
})

const showOrdersDrawer = computed(() => route.query.drawer === 'orders')

const handleOrdersDrawerUpdate = (value: boolean): void => {
  if (!value) {
    const q = { ...route.query }
    delete q.drawer
    router.replace({ path: route.path, query: q })
  }
}

const handleOrderClosed = (): void => {
  // Active reservation already cleared inside MyOrdersDrawerContent
  // Also clear reservation_id from URL query if present
  const q = { ...route.query }
  delete q.drawer
  delete q.reservation_id
  delete q.table_no
  router.replace({ path: route.path, query: q })
}

// When drawer is open, use fresh storage so we have reservation even when opening from Profile/Order (no timing issue)
const drawerReservation = computed(() =>
  showOrderDrawer.value ? getStoredActiveReservation() : null
)

const reservationForDrawer = computed(() => drawerReservation.value ?? storedReservation.value)

const canPlaceOrderFromStorage = computed(() => {
  return !!(
    authStore.isAuthenticated &&
    reservationForDrawer.value?.reservationId &&
    !sessionExpiredForOrderDrawer.value
  )
})

const handleOrderDrawerUpdate = (value: boolean): void => {
  if (!value) {
    const q = { ...route.query }
    delete q.drawer
    router.replace({ path: route.path, query: q })
  }
}

const handlePlaceOrderFromDrawer = async (): Promise<void> => {
  const reservationId =
    (route.query.reservation_id as string) || reservationForDrawer.value?.reservationId
  if (!reservationId) {
    showError('No active reservation. Please scan the table QR or use your reservation link.')
    return
  }
  isPlacingOrderFromDrawer.value = true
  try {
    const success = await placeOrder(reservationId)
    if (success) {
      handleOrderDrawerUpdate(false)
    }
  } finally {
    isPlacingOrderFromDrawer.value = false
  }
}

const handleOrderDrawerLogin = (): void => {
  handleOrderDrawerUpdate(false)
  router.push({ path: route.path, query: { ...route.query, drawer: 'login' } })
}

const handleOrderDrawerSignup = (): void => {
  handleOrderDrawerUpdate(false)
  router.push({ path: route.path, query: { ...route.query, drawer: 'register' } })
}

const handleOrderCompleteFromDrawer = (): void => {
  handleOrderDrawerUpdate(false)
}

const handleGuestSuccessFromDrawer = (reservationId: string): void => {
  refreshStoredReservation()
  router.replace({
    path: route.path,
    query: { ...route.query, reservation_id: reservationId },
  })
}

const handleTableUnavailableFromCart = (): void => {
  handleOrderDrawerUpdate(false)
  showError('This table is booked by someone else. Please contact the waiter or restaurant.')
}

function refreshStoredReservation(): void {
  storedReservation.value = getStoredActiveReservation()
}

async function validateActiveReservationForOrderDrawer(): Promise<void> {
  sessionExpiredForOrderDrawer.value = false
  const info = getStoredActiveReservation()
  const reservationId = info?.reservationId
  const id = reservationId ? Number(reservationId) : NaN
  if (Number.isNaN(id)) return
  const validation = await validateReservationStillActive(id)
  if (!validation.ok) {
    sessionExpiredForOrderDrawer.value = true
    refreshStoredReservation()
    showError(validation.message)
  }
}

onMounted(() => {
  refreshStoredReservation()
})

watch(
  () => route.query.drawer,
  () => refreshStoredReservation(),
  { immediate: false }
)

watch(
  () => showOrderDrawer.value,
  open => {
    if (!open) {
      sessionExpiredForOrderDrawer.value = false
    }
  }
)

watch(
  () => showOrderDrawer.value,
  open => {
    if (open) {
      void validateActiveReservationForOrderDrawer()
    }
  }
)

// When header cart dropdown triggers openCartModal(), handle it for all pages EXCEPT RestaurantMenu
// (RestaurantMenu is handled by MenuView.vue itself).
// Pushing drawer=order into the URL triggers the showOrderDrawer computed and the validation watcher above.
watch(
  () => orderStore.showOrderPanel,
  open => {
    if (!open || route.name === 'RestaurantMenu') return
    router.push({ path: route.path, query: { ...route.query, drawer: 'order' } })
    orderStore.closeCartModal()
  }
)

// Routes that should show bottom nav (exclude Home page)
const routesWithBottomNav = [
  'Restaurants',
  'MyOrders',
  'MyReservations',
  'Profile',
  'RestaurantMenu',
  'Cart',
]

const handleResize = (): void => {
  if (typeof window !== 'undefined') {
    windowWidth.value = window.innerWidth
  }
}

onMounted(() => {
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', handleResize)
    windowWidth.value = window.innerWidth
  }
})

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', handleResize)
  }
})

// Whether nav should show at all (auth + route check, no screen-size gate)
const showNavForRoute = computed(() => {
  if (!authStore.isAuthenticated) return false
  if (route.meta.showBottomNav !== undefined) return route.meta.showBottomNav === true
  return routesWithBottomNav.includes(route.name as string)
})

// Mobile bottom nav: only on small screens
const showBottomNav = computed(() => showNavForRoute.value && windowWidth.value < 768)

// Desktop floating nav: only on large screens (WebFloatingNav handles its own md:flex class,
// but we still gate it here so it isn't mounted unnecessarily on mobile)
const showFloatingNav = computed(() => showNavForRoute.value && windowWidth.value >= 768)

const showHeader = computed(() => {
  return route.meta.hideHeader !== true
})
</script>
